import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Clock, Activity } from "lucide-react";

import { useTranslation } from "react-i18next";
/** Compact Attendance summary card for Manager/Dealer dashboard.
 * Shows active-now count, hours worked today, and a small list of who's on shift.
 */
export default function AttendanceSummaryCard() {
  const { t: __t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get("/dealer/attendance/summary")
        .then((r) => { if (!cancelled) setData(r.data); })
        .catch(() => {})
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <Card className="border-emerald-200/60 bg-gradient-to-br from-emerald-50/40 to-transparent" data-testid="attendance-summary-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500 text-white">
              <Users className="h-4 w-4" />
            </div>
            <span>{__t("components.attendancesummarycard.attendance_today")}</span>
          </div>
          <Link to="/dealer/attendance" className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 hover:text-emerald-900" data-testid="attendance-view-all">View all →</Link>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-3 text-xs text-muted-foreground">{__t("common.loading")}</div>
        ) : !data ? (
          <div className="py-3 text-xs text-muted-foreground">{__t("components.attendancesummarycard.attendance_data_not_available")}</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-md bg-background/60 p-2" data-testid="attendance-active-now">
                <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{__t("components.attendancesummarycard.active_now")}</div>
                <div className="font-display text-xl font-bold tabular-nums text-emerald-700">{data.active_now}</div>
              </div>
              <div className="rounded-md bg-background/60 p-2" data-testid="attendance-hours-today">
                <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{__t("components.attendancesummarycard.hours_today")}</div>
                <div className="font-display text-xl font-bold tabular-nums text-foreground">{data.hours_worked_today.toFixed(1)}</div>
              </div>
              <div className="rounded-md bg-background/60 p-2" data-testid="attendance-staff-total">
                <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{__t("components.attendancesummarycard.total_staff")}</div>
                <div className="font-display text-xl font-bold tabular-nums text-muted-foreground">{data.staff_total}</div>
              </div>
            </div>
            {data.active_list && data.active_list.length > 0 ? (
              <div className="mt-3 space-y-1">
                {data.active_list.slice(0, 4).map((a) => {
                  const backendUrl = process.env.REACT_APP_BACKEND_URL;
                  const photoSrc = a.photo_url
                    ? (a.photo_url.startsWith("http") ? a.photo_url : `${backendUrl}${a.photo_url}`)
                    : null;
                  return (
                    <div key={a.user_id} className="flex items-center justify-between rounded bg-background/40 px-2 py-1 text-xs">
                      <div className="flex items-center gap-1.5">
                        {photoSrc ? (
                          <img
                            src={photoSrc}
                            alt={a.name}
                            className="h-5 w-5 rounded-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        ) : (
                          <Activity className="h-3 w-3 animate-pulse text-emerald-500" />
                        )}
                        <span className="font-semibold text-foreground">{a.name}</span>
                      </div>
                      <div className="flex items-center gap-1 font-mono text-[10px] text-muted-foreground">
                        <Clock className="h-2.5 w-2.5" />
                        {a.checked_in_at ? new Date(a.checked_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                      </div>
                    </div>
                  );
                })}
                {data.active_list.length > 4 && (
                  <div className="pt-1 text-center text-[10px] text-muted-foreground">+ {data.active_list.length - 4} more</div>
                )}
              </div>
            ) : (
              <div className="mt-3 rounded-md bg-background/40 p-2 text-center text-[11px] text-muted-foreground">{__t("components.attendancesummarycard.no_one_is_checked_in_right_now")}</div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
